import React, { useState } from 'react';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mapChangePasswordError, CHANGE_PASSWORD_SESSION_EXPIRED } from '../lib/changePasswordErrors';
import { PASSWORD_REQUIREMENTS_HELPER, validateChangePasswordForm } from '../lib/passwordPolicy';
import { supabase } from '../lib/supabaseClient';

export default function SecurityPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const clearForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setMessage('');
    setError('');
  };

  const handleCancel = () => {
    if (loading) return;
    clearForm();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    setMessage('');

    const validationError = validateChangePasswordForm({
      currentPassword,
      newPassword,
      confirmPassword,
    });

    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setLoading(true);

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) throw userError;

      const email = String(user?.email || '').trim();
      if (!email) {
        setError(CHANGE_PASSWORD_SESSION_EXPIRED);
        return;
      }

      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword,
      });
      if (verifyError) throw verifyError;

      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setMessage('Password changed successfully.');
    } catch (updateErr) {
      setError(mapChangePasswordError(updateErr));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-[100dvh] overflow-y-auto bg-[#141414] text-white px-5 py-4">
      <div className="mx-auto flex min-h-full max-w-sm flex-col">
        <div className="relative flex items-center justify-center pt-1">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="absolute left-0 inline-flex h-10 w-10 items-center justify-center rounded-full text-white/90 transition hover:bg-white/5"
            aria-label="Go back"
          >
            <ArrowLeft size={22} />
          </button>

          <h1 className="text-[28px] font-semibold tracking-tight text-white">Security</h1>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col justify-start pt-14 pb-8" noValidate>
          <div>
            <h2 className="text-[14px] font-semibold text-white">Change Password</h2>

            <div className="mt-3 space-y-3">
              <PasswordInput
                label="Current Password"
                value={currentPassword}
                onChange={setCurrentPassword}
                shown={showCurrentPassword}
                toggle={() => setShowCurrentPassword((prev) => !prev)}
                autoComplete="current-password"
                disabled={loading}
              />

              <PasswordInput
                label="New Password"
                value={newPassword}
                onChange={setNewPassword}
                shown={showNewPassword}
                toggle={() => setShowNewPassword((prev) => !prev)}
                autoComplete="new-password"
                disabled={loading}
                helperText={PASSWORD_REQUIREMENTS_HELPER}
              />

              <PasswordInput
                label="Confirm New Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                shown={showConfirmPassword}
                toggle={() => setShowConfirmPassword((prev) => !prev)}
                autoComplete="new-password"
                disabled={loading}
              />
            </div>

            {message ? <p className="mt-4 text-xs font-medium text-emerald-300">{message}</p> : null}
            {error ? <p className="mt-4 text-xs font-medium text-rose-300">{error}</p> : null}
          </div>

          <div className="flex-1" />

          <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              className="inline-flex min-h-[48px] w-full items-center justify-center rounded-[14px] border border-white/15 px-5 py-3 text-sm font-semibold text-white/80 transition hover:bg-white/5 disabled:opacity-60 sm:w-auto sm:min-w-[140px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex min-h-[48px] w-full items-center justify-center rounded-[14px] bg-[#1268ff] px-5 py-3 text-sm font-semibold text-white shadow-[0_12px_24px_rgba(18,104,255,0.25)] transition hover:bg-[#0f5ee5] disabled:opacity-60 sm:w-auto sm:min-w-[180px]"
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const PasswordInput = ({ label, value, onChange, shown, toggle, autoComplete, disabled, helperText }) => {
  return (
    <div>
      <div className="rounded-[12px] border border-white/10 bg-white shadow-[0_8px_18px_rgba(0,0,0,0.18)]">
        <div className="relative">
          <input
            type={shown ? 'text' : 'password'}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={label}
            autoComplete={autoComplete}
            disabled={disabled}
            className="w-full rounded-[12px] bg-transparent px-4 py-3 pr-11 text-[13px] text-[#151515] placeholder:text-[#b8b8b8] focus:outline-none disabled:opacity-60"
          />
          <button
            type="button"
            onClick={toggle}
            disabled={disabled}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#b2b2b2] transition hover:text-[#666] disabled:opacity-60"
            aria-label={shown ? `Hide ${label}` : `Show ${label}`}
          >
            {shown ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>
      {helperText ? <p className="mt-1 text-[11px] text-white/45">{helperText}</p> : null}
    </div>
  );
};
